import { useEffect, useState, createContext, useContext } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { 
  Search, Menu, X, Moon, Sun, ChevronDown, Mail, 
  MessageCircle, Share2, Facebook, Twitter, Linkedin, 
  Copy, Clock, Eye, ArrowLeft, Send, Heart, BookOpen, ChevronRight,
  Plus, Edit, Trash2, LogOut, Save, Image, FileText, Settings, LayoutDashboard
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Toaster, toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Theme Context
const ThemeContext = createContext();

const ThemeProvider = ({ children }) => {
  const [theme, setTheme] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('theme') || 'light';
    }
    return 'light';
  });

  useEffect(() => {
    const root = window.document.documentElement;
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    localStorage.setItem('theme', theme);
  }, [theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
};

const useTheme = () => useContext(ThemeContext);

// Navigation Component with Dropdowns
const Navigation = ({ onOpenSearch }) => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [storiesOpen, setStoriesOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();

  return (
    <header className="bg-card border-b border-border sticky top-0 z-50" data-testid="navigation">
      <nav className="max-w-5xl mx-auto px-4 sm:px-6">
        <div className="flex items-center justify-center py-4 relative">
          {/* Desktop Navigation - Centered */}
          <div className="hidden md:flex items-center gap-8">
            <Link 
              to="/" 
              className="text-sm font-medium text-primary hover:text-primary/80 transition-colors uppercase tracking-wider"
              data-testid="nav-home"
            >
              Home
            </Link>
            
            {/* Stories Dropdown */}
            <DropdownMenu open={storiesOpen} onOpenChange={setStoriesOpen}>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider" data-testid="nav-stories">
                Stories <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-48">
                <DropdownMenuItem asChild>
                  <Link to="/category/life" className="w-full cursor-pointer">Life</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/category/living-abroad" className="w-full cursor-pointer">Living Abroad</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/category/work" className="w-full cursor-pointer">Work</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/category/nostalgia" className="w-full cursor-pointer">Nostalgia</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <Link to="/all-stories" className="w-full cursor-pointer font-medium">All Stories</Link>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Book Dropdown */}
            <DropdownMenu>
              <DropdownMenuTrigger className="flex items-center gap-1 text-sm font-medium text-muted-foreground hover:text-primary transition-colors uppercase tracking-wider" data-testid="nav-book">
                Book <ChevronDown className="h-4 w-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="center" className="w-56">
                <DropdownMenuItem asChild>
                  <Link to="/book" className="w-full cursor-pointer">About the Book</Link>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="https://www.amazon.com/dp/B08H5X8S3K" target="_blank" rel="noopener noreferrer" className="w-full cursor-pointer">Buy on Amazon</a>
                </DropdownMenuItem>
                <DropdownMenuItem asChild>
                  <a href="https://notionpress.com/read/short-stories-from-the-gulmohar-days" target="_blank" rel="noopener noreferrer" className="w-full cursor-pointer">Buy on Notion Press (India)</a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>

          {/* Actions - Right side */}
          <div className="absolute right-0 flex items-center gap-2">
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={toggleTheme}
              className="rounded-full text-muted-foreground hover:text-primary"
              data-testid="nav-theme-toggle"
            >
              {theme === 'light' ? <Moon className="h-5 w-5" /> : <Sun className="h-5 w-5" />}
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              onClick={onOpenSearch}
              className="rounded-full text-muted-foreground hover:text-primary"
              data-testid="nav-search-btn"
            >
              <Search className="h-5 w-5" />
            </Button>
            <Button 
              variant="ghost" 
              size="icon" 
              className="md:hidden rounded-full"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="nav-mobile-menu"
            >
              {mobileMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="md:hidden py-4 border-t border-border animate-fade-in" data-testid="mobile-menu">
            <div className="flex flex-col gap-3">
              <Link to="/" onClick={() => setMobileMenuOpen(false)} className="text-sm font-medium text-primary uppercase tracking-wider py-2">Home</Link>
              <div className="border-t border-border pt-2">
                <span className="text-xs text-muted-foreground uppercase">Stories</span>
                <Link to="/category/life" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4">Life</Link>
                <Link to="/category/living-abroad" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4">Living Abroad</Link>
                <Link to="/category/work" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4">Work</Link>
                <Link to="/category/nostalgia" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4">Nostalgia</Link>
                <Link to="/all-stories" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4 font-medium">All Stories</Link>
              </div>
              <div className="border-t border-border pt-2">
                <span className="text-xs text-muted-foreground uppercase">Book</span>
                <Link to="/book" onClick={() => setMobileMenuOpen(false)} className="block text-sm py-2 pl-4">About the Book</Link>
              </div>
            </div>
          </div>
        )}
      </nav>
    </header>
  );
};

// Hero Section with Original Banner
const Hero = () => {
  const [bannerImage, setBannerImage] = useState('https://customer-assets.emergentagent.com/job_thisandthat-improved/artifacts/yrbee7pe_ChatGPT%20Image%20Apr%208%2C%202026%2C%2011_09_42%20PM.png');

  useEffect(() => {
    const savedBanner = localStorage.getItem('homepageBanner');
    if (savedBanner) setBannerImage(savedBanner);
  }, []);

  return (
    <section className="relative" data-testid="hero-section">
      {/* Original Header Banner - Indian inspired illustration */}
      <div className="w-full h-[25vh] bg-[#f8f4ef] dark:bg-[#2a2520] flex items-center justify-center overflow-hidden">
        <img 
          src={bannerImage} 
          alt="This and That Stories - Indian inspired illustration with traditional couple, train, coffee cups, and the author"
          className="w-full h-full object-contain"
        />
      </div>
      
      {/* Content */}
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12 animate-fade-in-up">
        <div className="bg-card rounded-lg p-8 md:p-12 shadow-sm border border-border">
          <p className="text-foreground text-lg mb-6 leading-relaxed">
            <strong className="text-primary">This and That Stories</strong> is a blog that celebrates the absurd, heartwarming, and nostalgic moments of everyday life. From office oddities to 90s childhood memories, from the quirks of living abroad to the joys of robot vacuum companionship—each story is a little slice of real life, served with humor and heart.
          </p>
          <p className="text-foreground text-lg mb-6 leading-relaxed">
            Whether you're an expat, a nostalgic 90s kid, or someone who's just had <em>one of those weeks</em>, you'll find something here to make you smile, nod, or laugh out loud.
          </p>
          <p className="text-muted-foreground italic text-lg">
            Welcome to a blog where it's okay to be a little chaotic, curious, and caffeinated.
          </p>
        </div>
      </div>
    </section>
  );
};

// Blog Card Component
const BlogCard = ({ post, featured = false }) => {
  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  if (featured) {
    return (
      <Link to={`/post/${post.slug}`} className="group block" data-testid={`featured-post-${post.slug}`}>
        <article className="relative overflow-hidden rounded-2xl bg-card border border-border transition-all duration-500 hover:shadow-xl hover:-translate-y-1">
          <div className="aspect-[16/9] overflow-hidden">
            <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent" />
          </div>
          <div className="absolute bottom-0 left-0 right-0 p-6">
            {post.category_name && (
              <span className="inline-block px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full mb-3">{post.category_name}</span>
            )}
            <h2 className="text-xl md:text-2xl font-serif font-bold text-white mb-2 group-hover:text-primary transition-colors line-clamp-2">{post.title}</h2>
            <div className="flex items-center gap-4 text-white/60 text-sm">
              <span>{formatDate(post.created_at)}</span>
              <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{post.read_time} min</span>
            </div>
          </div>
        </article>
      </Link>
    );
  }

  return (
    <Link to={`/post/${post.slug}`} className="group block" data-testid={`post-card-${post.slug}`}>
      <article className="overflow-hidden rounded-xl bg-card border border-border transition-all duration-300 hover:shadow-lg hover:-translate-y-1">
        <div className="aspect-[4/3] overflow-hidden">
          <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105" />
        </div>
        <div className="p-5">
          {post.category_name && (
            <span className="inline-block px-2 py-0.5 text-xs font-medium bg-primary/10 text-primary rounded-full mb-2">{post.category_name}</span>
          )}
          <h3 className="text-lg font-serif font-bold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">{post.title}</h3>
          <p className="text-muted-foreground text-sm line-clamp-2 mb-3">{post.excerpt}</p>
          <div className="flex items-center justify-between text-xs text-muted-foreground">
            <span>{formatDate(post.created_at)}</span>
            <span className="flex items-center gap-1"><Clock className="h-3 w-3" />{post.read_time} min</span>
          </div>
        </div>
      </article>
    </Link>
  );
};

// Newsletter Modal
const NewsletterModal = ({ isOpen, onClose }) => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await axios.post(`${API}/newsletter/subscribe`, { email });
      toast.success("You're subscribed! Welcome to the family.");
      setEmail('');
      onClose();
    } catch (error) {
      if (error.response?.status === 400) {
        toast.error("You're already subscribed!");
      } else {
        toast.error("Oops! Something went wrong.");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="newsletter-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif">Join the Story Circle</DialogTitle>
          <DialogDescription>Get new stories delivered to your inbox. No spam, just tales worth reading.</DialogDescription>
        </DialogHeader>
        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <Input type="email" placeholder="your@email.com" value={email} onChange={(e) => setEmail(e.target.value)} required className="rounded-full" data-testid="newsletter-email-input" />
          <Button type="submit" className="w-full rounded-full bg-primary text-primary-foreground" disabled={loading} data-testid="newsletter-submit-btn">
            {loading ? 'Subscribing...' : 'Subscribe'}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

// Search Modal
const SearchModal = ({ isOpen, onClose }) => {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleSearch = async (searchQuery) => {
    if (searchQuery.length < 2) { setResults([]); return; }
    setLoading(true);
    try {
      const response = await axios.get(`${API}/search?q=${encodeURIComponent(searchQuery)}`);
      setResults(response.data);
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const debounce = setTimeout(() => { handleSearch(query); }, 300);
    return () => clearTimeout(debounce);
  }, [query]);

  const handleResultClick = (slug) => {
    navigate(`/post/${slug}`);
    onClose();
    setQuery('');
    setResults([]);
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-2xl" data-testid="search-modal">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif">Search Stories</DialogTitle>
        </DialogHeader>
        <div className="mt-4">
          <div className="relative">
            <Search className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-muted-foreground" />
            <Input type="text" placeholder="Search for stories..." value={query} onChange={(e) => setQuery(e.target.value)} className="pl-12 rounded-full text-lg py-6" autoFocus data-testid="search-input" />
          </div>
          {loading && <div className="flex justify-center py-8"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div></div>}
          {!loading && results.length > 0 && (
            <div className="mt-4 max-h-96 overflow-y-auto space-y-2" data-testid="search-results">
              {results.map((post) => (
                <button key={post.id} onClick={() => handleResultClick(post.slug)} className="w-full text-left p-4 rounded-xl hover:bg-muted transition-colors flex gap-4" data-testid={`search-result-${post.slug}`}>
                  <img src={post.featured_image} alt={post.title} className="w-20 h-16 object-cover rounded-lg flex-shrink-0" />
                  <div>
                    <h4 className="font-serif font-bold text-foreground">{post.title}</h4>
                    <p className="text-sm text-muted-foreground line-clamp-1">{post.excerpt}</p>
                  </div>
                </button>
              ))}
            </div>
          )}
          {!loading && query.length >= 2 && results.length === 0 && (
            <div className="text-center py-8 text-muted-foreground">
              <BookOpen className="h-12 w-12 mx-auto mb-2 opacity-50" />
              <p>No stories found for "{query}"</p>
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Share Button
const ShareButton = ({ url, title }) => {
  const shareOnFacebook = () => window.open(`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`, '_blank');
  const shareOnTwitter = () => window.open(`https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(title)}`, '_blank');
  const shareOnLinkedin = () => window.open(`https://www.linkedin.com/shareArticle?mini=true&url=${encodeURIComponent(url)}&title=${encodeURIComponent(title)}`, '_blank');
  const copyLink = () => { navigator.clipboard.writeText(url); toast.success('Link copied!'); };

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="outline" size="sm" className="rounded-full" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" />Share</Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-48">
        <DropdownMenuItem onClick={shareOnFacebook}><Facebook className="h-4 w-4 mr-2" />Facebook</DropdownMenuItem>
        <DropdownMenuItem onClick={shareOnTwitter}><Twitter className="h-4 w-4 mr-2" />Twitter</DropdownMenuItem>
        <DropdownMenuItem onClick={shareOnLinkedin}><Linkedin className="h-4 w-4 mr-2" />LinkedIn</DropdownMenuItem>
        <DropdownMenuItem onClick={copyLink}><Copy className="h-4 w-4 mr-2" />Copy Link</DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

// Footer
const Footer = ({ onOpenNewsletter }) => {
  return (
    <footer className="bg-muted py-16" data-testid="footer">
      <div className="max-w-6xl mx-auto px-4 sm:px-6">
        <div className="grid md:grid-cols-4 gap-8">
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">This & That Stories</h4>
            <p className="text-muted-foreground text-sm mb-4">Celebrating the absurd, heartwarming, and nostalgic moments of everyday life.</p>
            <Button onClick={onOpenNewsletter} size="sm" className="rounded-full bg-primary text-primary-foreground" data-testid="footer-subscribe-btn">
              <Mail className="h-4 w-4 mr-2" />Subscribe
            </Button>
          </div>
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">Stories</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/category/life" className="text-muted-foreground hover:text-primary transition-colors">Life</Link></li>
              <li><Link to="/category/living-abroad" className="text-muted-foreground hover:text-primary transition-colors">Living Abroad</Link></li>
              <li><Link to="/category/work" className="text-muted-foreground hover:text-primary transition-colors">Work</Link></li>
              <li><Link to="/category/nostalgia" className="text-muted-foreground hover:text-primary transition-colors">Nostalgia</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">Quick Links</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/about" className="text-muted-foreground hover:text-primary transition-colors">About</Link></li>
              <li><Link to="/book" className="text-muted-foreground hover:text-primary transition-colors">Book</Link></li>
              <li><Link to="/contact" className="text-muted-foreground hover:text-primary transition-colors">Contact</Link></li>
            </ul>
          </div>
          <div>
            <h4 className="font-serif font-bold text-lg mb-4">Legal</h4>
            <ul className="space-y-2 text-sm">
              <li><Link to="/privacy-policy" className="text-muted-foreground hover:text-primary transition-colors">Privacy Policy</Link></li>
              <li><Link to="/terms-of-service" className="text-muted-foreground hover:text-primary transition-colors">Terms of Service</Link></li>
            </ul>
          </div>
        </div>
        <div className="border-t border-border mt-12 pt-8 text-center text-muted-foreground text-sm">
          <p>© {new Date().getFullYear()} This and That Stories by Nishanth S Coontoor. Made with ❤️ and too much coffee.</p>
        </div>
      </div>
    </footer>
  );
};

// Home Page
const Home = () => {
  const [featuredPosts, setFeaturedPosts] = useState([]);
  const [recentPosts, setRecentPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await axios.post(`${API}/seed`);
        const [featuredRes, postsRes, categoriesRes] = await Promise.all([
          axios.get(`${API}/posts/featured`),
          axios.get(`${API}/posts?limit=12`),
          axios.get(`${API}/categories`)
        ]);
        setFeaturedPosts(featuredRes.data);
        setRecentPosts(postsRes.data.filter(p => !p.featured));
        setCategories(categoriesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" data-testid="home-loading">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Loading stories...</p>
        </div>
      </div>
    );
  }

  return (
    <main data-testid="home-page">
      <Hero />
      
      {featuredPosts.length > 0 && (
        <section className="py-12 px-4 sm:px-6" data-testid="featured-section">
          <div className="max-w-6xl mx-auto">
            <div className="flex items-center justify-between mb-8">
              <h2 className="text-2xl font-serif font-bold text-foreground">Featured Stories</h2>
              <span className="text-primary text-sm italic">Hot off the press!</span>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {featuredPosts.map(post => (
                <BlogCard key={post.id} post={post} featured />
              ))}
            </div>
          </div>
        </section>
      )}

      <section className="py-8 bg-muted/50" data-testid="categories-section">
        <div className="max-w-6xl mx-auto px-4 sm:px-6">
          <div className="flex flex-wrap justify-center gap-3">
            {categories.map(category => (
              <Link key={category.id} to={`/category/${category.slug}`} className="px-4 py-2 bg-card rounded-full border border-border hover:border-primary hover:text-primary transition-colors text-sm font-medium" data-testid={`category-${category.slug}`}>
                {category.name} <span className="text-muted-foreground">({category.post_count})</span>
              </Link>
            ))}
          </div>
          <div className="text-center mt-6">
            <Link to="/all-stories">
              <Button variant="outline" className="rounded-full">View All Stories <ChevronRight className="h-4 w-4 ml-1" /></Button>
            </Link>
          </div>
        </div>
      </section>
    </main>
  );
};

// All Stories Page
const AllStoriesPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPosts = async () => {
      try {
        const response = await axios.get(`${API}/posts?limit=50`);
        setPosts(response.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPosts();
  }, []);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;

  return (
    <main className="py-12 px-4 sm:px-6" data-testid="all-stories-page">
      <div className="max-w-6xl mx-auto">
        <h1 className="text-4xl font-serif font-bold mb-2">All Stories</h1>
        <p className="text-muted-foreground mb-8">Personal narratives and funny short stories</p>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {posts.map(post => <BlogCard key={post.id} post={post} />)}
        </div>
      </div>
    </main>
  );
};

// Category Page
const CategoryPage = () => {
  const { slug } = useParams();
  const [posts, setPosts] = useState([]);
  const [category, setCategory] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      try {
        const categoriesRes = await axios.get(`${API}/categories`);
        const cat = categoriesRes.data.find(c => c.slug === slug);
        setCategory(cat);
        if (cat) {
          const postsRes = await axios.get(`${API}/posts?category=${cat.id}`);
          setPosts(postsRes.data);
        }
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [slug]);

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  if (!category) return <div className="min-h-screen flex items-center justify-center pt-24"><div className="text-center"><h1 className="text-2xl font-serif font-bold mb-2">Category Not Found</h1><Link to="/" className="text-primary hover:underline">Go back home</Link></div></div>;

  return (
    <main className="py-12 px-4 sm:px-6" data-testid="category-page">
      <div className="max-w-6xl mx-auto">
        <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-6"><ArrowLeft className="h-4 w-4 mr-2" />Back to Home</Link>
        <h1 className="text-4xl font-serif font-bold mb-2">{category.name}</h1>
        {category.description && <p className="text-muted-foreground mb-8">{category.description}</p>}
        {posts.length > 0 ? (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {posts.map(post => <BlogCard key={post.id} post={post} />)}
          </div>
        ) : (
          <div className="text-center py-16"><BookOpen className="h-16 w-16 mx-auto text-muted-foreground/50 mb-4" /><p className="text-muted-foreground">No stories in this category yet.</p></div>
        )}
      </div>
    </main>
  );
};

// Single Post Page
const PostPage = () => {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [commentForm, setCommentForm] = useState({ author_name: '', author_email: '', content: '' });
  const [submittingComment, setSubmittingComment] = useState(false);

  useEffect(() => {
    const fetchPost = async () => {
      setLoading(true);
      try {
        const response = await axios.get(`${API}/posts/${slug}`);
        setPost(response.data);
        const commentsRes = await axios.get(`${API}/posts/${response.data.id}/comments`);
        setComments(commentsRes.data);
      } catch (error) {
        console.error('Error:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  const handleCommentSubmit = async (e) => {
    e.preventDefault();
    setSubmittingComment(true);
    try {
      const response = await axios.post(`${API}/comments`, { ...commentForm, post_id: post.id });
      setComments([response.data, ...comments]);
      setCommentForm({ author_name: '', author_email: '', content: '' });
      toast.success('Comment posted!');
    } catch (error) {
      toast.error('Failed to post comment.');
    } finally {
      setSubmittingComment(false);
    }
  };

  const formatDate = (dateString) => new Date(dateString).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' });

  if (loading) return <div className="min-h-screen flex items-center justify-center pt-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  if (!post) return <div className="min-h-screen flex items-center justify-center pt-24"><div className="text-center"><h1 className="text-2xl font-serif font-bold mb-2">Story Not Found</h1><Link to="/" className="text-primary hover:underline">Go back home</Link></div></div>;

  return (
    <main data-testid="post-page">
      <div className="relative h-[40vh] md:h-[50vh] overflow-hidden">
        <img src={post.featured_image} alt={post.title} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/50 to-transparent" />
      </div>
      <article className="max-w-3xl mx-auto px-4 sm:px-6 -mt-24 relative z-10">
        <header className="mb-8">
          <Link to="/" className="inline-flex items-center text-muted-foreground hover:text-foreground transition-colors mb-4"><ArrowLeft className="h-4 w-4 mr-2" />Back to Stories</Link>
          {post.category_name && <Link to={`/category/${post.category_id}`} className="inline-block px-3 py-1 text-xs font-medium bg-primary text-primary-foreground rounded-full mb-4 ml-2">{post.category_name}</Link>}
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-foreground mb-4" data-testid="post-title">{post.title}</h1>
          <div className="flex flex-wrap items-center gap-4 text-muted-foreground text-sm mb-4">
            <span className="font-medium text-foreground">{post.author}</span>
            <span>•</span>
            <span>{formatDate(post.created_at)}</span>
            <span className="flex items-center gap-1"><Clock className="h-4 w-4" />{post.read_time} min</span>
            <span className="flex items-center gap-1"><Eye className="h-4 w-4" />{post.views}</span>
          </div>
          <ShareButton url={window.location.href} title={post.title} />
        </header>
        <div className="prose max-w-none bg-card p-6 md:p-8 rounded-xl border border-border" dangerouslySetInnerHTML={{ __html: post.content }} data-testid="post-content" />
        
        <section className="mt-12" data-testid="comments-section">
          <h3 className="text-xl font-serif font-bold mb-6 flex items-center gap-2"><MessageCircle className="h-5 w-5" />Comments ({comments.length})</h3>
          <form onSubmit={handleCommentSubmit} className="mb-8 p-6 bg-card rounded-xl border border-border" data-testid="comment-form">
            <div className="grid md:grid-cols-2 gap-4 mb-4">
              <Input placeholder="Your name" value={commentForm.author_name} onChange={(e) => setCommentForm({ ...commentForm, author_name: e.target.value })} required data-testid="comment-name-input" />
              <Input type="email" placeholder="Your email" value={commentForm.author_email} onChange={(e) => setCommentForm({ ...commentForm, author_email: e.target.value })} required data-testid="comment-email-input" />
            </div>
            <Textarea placeholder="Share your thoughts..." value={commentForm.content} onChange={(e) => setCommentForm({ ...commentForm, content: e.target.value })} required className="mb-4 min-h-[100px]" data-testid="comment-content-input" />
            <Button type="submit" className="rounded-full" disabled={submittingComment} data-testid="comment-submit-btn">{submittingComment ? 'Posting...' : <><Send className="h-4 w-4 mr-2" />Post Comment</>}</Button>
          </form>
          <div className="space-y-4">
            {comments.map(comment => (
              <div key={comment.id} className="p-4 bg-muted rounded-xl">
                <div className="flex items-center gap-3 mb-2">
                  <div className="w-8 h-8 rounded-full bg-primary/20 flex items-center justify-center"><span className="font-serif font-bold text-primary text-sm">{comment.author_name.charAt(0).toUpperCase()}</span></div>
                  <span className="font-medium text-foreground">{comment.author_name}</span>
                  <span className="text-xs text-muted-foreground">{formatDate(comment.created_at)}</span>
                </div>
                <p className="text-foreground text-sm">{comment.content}</p>
              </div>
            ))}
            {comments.length === 0 && <p className="text-center text-muted-foreground py-4">Be the first to share your thoughts!</p>}
          </div>
        </section>
      </article>
    </main>
  );
};

// About Page
const AboutPage = () => (
  <main className="py-12 px-4 sm:px-6" data-testid="about-page">
    <div className="max-w-3xl mx-auto">
      <h1 className="text-4xl font-serif font-bold mb-6">About</h1>
      <div className="prose max-w-none">
        <p className="text-lg text-muted-foreground mb-6">Welcome to a blog where it's okay to be a little chaotic, curious, and caffeinated.</p>
        <p><strong>This and That Stories</strong> is a digital scrapbook celebrating the absurd, heartwarming, and nostalgic moments of everyday life. From office oddities to 90s childhood memories, from the quirks of living abroad to the joys of robot vacuum companionship—each story is a little slice of real life, served with humor and heart.</p>
        <h2>The Storyteller</h2>
        <p>Hi, I'm <strong>Nishanth S Coontoor</strong>. I'm just someone who notices things—the weird things, the funny things, the things that make you go "oh, that's just me then?" (Spoiler: it's never just you.)</p>
        <p>After years of collecting stories in notebooks, group chats, and rambling voice memos to friends at 2 AM, I decided to put them somewhere more permanent.</p>
        <h2>What You'll Find Here</h2>
        <ul>
          <li><strong>Life:</strong> The absurd and heartwarming moments of everyday existence.</li>
          <li><strong>Living Abroad:</strong> Tales from expat life, cultural misunderstandings, and being slightly lost in translation.</li>
          <li><strong>Work:</strong> Corporate chaos, cubicle chronicles, and the absurdity of professional life.</li>
          <li><strong>Nostalgia:</strong> Remember when? 90s memories, childhood stories, and things that made us who we are.</li>
        </ul>
      </div>
    </div>
  </main>
);

// Book Page
const BookPage = () => (
  <main className="py-12 px-4 sm:px-6" data-testid="book-page">
    <div className="max-w-4xl mx-auto">
      <h1 className="text-4xl font-serif font-bold mb-2">Nostalgia in Paperback</h1>
      <p className="text-xl text-muted-foreground mb-8">A 90s Kid's Love Letter to Bangalore</p>
      
      {/* Book Cover and Buy Links */}
      <div className="grid md:grid-cols-2 gap-8 mb-12">
        <div className="flex justify-center">
          <img 
            src="https://m.media-amazon.com/images/I/81hdjU2NZxL._SY342_.jpg" 
            alt="Short Stories from the Gulmohar Days - Book Cover"
            className="rounded-lg shadow-xl max-w-full h-auto"
          />
        </div>
        <div className="flex flex-col justify-center">
          <p className="text-lg text-foreground mb-6">
            <strong>I published my first book!</strong> Available as ebook and paperback on Amazon. Available as paperback in India on Notion Press.
          </p>
          <p className="text-muted-foreground mb-6 italic">
            Step into 90s Bangalore through this delightful debut book filled with stories of MG Road walks, Cubbon Park train rides, and coin trades.
          </p>
          <div className="flex flex-col gap-3">
            <a href="https://www.amazon.com/dp/B08H5X8S3K" target="_blank" rel="noopener noreferrer">
              <Button className="w-full rounded-full bg-[#FF9900] hover:bg-[#FF9900]/90 text-black font-semibold">
                Buy on Amazon (Ebook & Paperback)
              </Button>
            </a>
            <a href="https://notionpress.com/read/short-stories-from-the-gulmohar-days" target="_blank" rel="noopener noreferrer">
              <Button variant="outline" className="w-full rounded-full">
                Buy on Notion Press (India Only)
              </Button>
            </a>
          </div>
        </div>
      </div>

      {/* About the Book */}
      <div className="bg-card p-8 rounded-xl border border-border mb-8">
        <h2 className="text-2xl font-serif font-bold mb-6">About the Book</h2>
        <div className="prose max-w-none text-foreground space-y-4">
          <p>
            <strong>Short Stories from the Gulmohar Days</strong> is about life in Bengaluru in the 90s. Most 90s kids like me will remember the days when we walked along the boulevard on MG Road eating ice cream and tagged along shopping sprees with our mothers to Safina Plaza.
          </p>
          <p>
            When we were not riding bikes or playing Dangerous Dave video game or trading coins with our friends, we visited Cubbon Park for the train ride.
          </p>
          <p>
            The idea to work on this specific theme was a result of conversations with my friends from school. Like many of you, these COVID times gave me a chance to catch up with them and we never ran out of stories to recall, share and laugh about. And thanks (?) to the quarantine life, there was no option left but to 'break' the 600-word limit barrier. It's not like I could run away to the outdoors like before.
          </p>
          <p className="italic text-muted-foreground">
            Thanks for choosing to stay thus far. I hope you enjoy this light read.
          </p>
        </div>
      </div>

      {/* The Journey to Writing This Book */}
      <div className="bg-muted p-8 rounded-xl mb-8">
        <h2 className="text-2xl font-serif font-bold mb-6">The Journey to Writing This Book</h2>
        <div className="prose max-w-none text-foreground space-y-4">
          <p>
            Many moons ago, when I was a student reporter with Bangalore Mirror, I got an opportunity to cover book releases for the features section. It was exciting and a welcome change because it meant I got to meet some well-known writers. The chance to interview and get a free signed copy was a bonus!
          </p>
          <p>
            Now, most book releases were on a Thursday evening after class. I clearly remember taking the BMTC bus route 36 to Majestic (Kempegowda Bus Station) and then hopping on to a red colored, air-conditioned bus to the Mayo Hall bus stop. While the bus to Majestic was almost always crowded, the quieter one to Mayo hall gave me a chance to think about what I'd ask this new writer.
          </p>
          <p>
            After I went through the standard set of questions, you know, the ones that make up a story's core, I'd get to the ones I was curious about.
          </p>
          <p className="italic pl-4 border-l-4 border-primary">
            How did you get the idea for this book?<br/>
            Did you face any writer's block?<br/>
            Did you sketch an outline for this book before you started writing?<br/>
            How did you think about the characters?
          </p>
          <p className="font-medium">
            None of these made it to print.
          </p>
          <p>
            You see, I was looking for stories of my own to tell. Just like this writer. But telling a story isn't as easy as it may seem.
          </p>
          <p>
            Strange though it may sound now, as a journalist, I was trained to be a 'fly on the wall.' This meant, my job was to observe and report, as is, while keeping my opinions to myself. Over the 3 years of reporting, I'd lost the ability to tell a story.
          </p>
          <p>
            Added to this was the struggle to go beyond the 600 words limit. Most stories in the newspaper were between 400-600 words and I had gotten used to sticking to this limit. So, a 20,000-word+ story was out of the picture.
          </p>
          <p className="font-medium italic">
            But once an item gets on your checklist, can you <em>really</em> let it go?
          </p>
        </div>
      </div>

      {/* Credits */}
      <div className="text-center text-muted-foreground">
        <p>The amazing cover art by <strong>Seema Misra</strong></p>
      </div>
    </div>
  </main>
);

// Contact Page
const ContactPage = () => {
  const [form, setForm] = useState({ name: '', email: '', message: '' });
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitting(true);
    setTimeout(() => {
      toast.success("Message sent! I'll get back to you soon.");
      setForm({ name: '', email: '', message: '' });
      setSubmitting(false);
    }, 1000);
  };

  return (
    <main className="py-12 px-4 sm:px-6" data-testid="contact-page">
      <div className="max-w-2xl mx-auto">
        <h1 className="text-4xl font-serif font-bold mb-4">Contact</h1>
        <p className="text-muted-foreground mb-8">Have a story to share? A question? Just want to say hi?</p>
        <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
          <div><label className="block text-sm font-medium mb-2">Name</label><Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium mb-2">Email</label><Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} required /></div>
          <div><label className="block text-sm font-medium mb-2">Message</label><Textarea value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} required className="min-h-[150px]" /></div>
          <Button type="submit" className="w-full rounded-full" disabled={submitting}>{submitting ? 'Sending...' : 'Send Message'}</Button>
        </form>
      </div>
    </main>
  );
};

// Privacy Policy Page
const PrivacyPolicyPage = () => (
  <main className="py-12 px-4 sm:px-6" data-testid="privacy-page">
    <div className="max-w-3xl mx-auto">
      <h1 className="text-4xl font-serif font-bold mb-6">Privacy Policy</h1>
      <div className="prose max-w-none">
        <p><em>Last updated: {new Date().toLocaleDateString()}</em></p>
        <h2>Introduction</h2>
        <p>This and That Stories ("we", "our", or "us") is committed to protecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.</p>
        <h2>Information We Collect</h2>
        <ul>
          <li><strong>Personal Information:</strong> When you subscribe to our newsletter or leave a comment, we collect your name and email address.</li>
          <li><strong>Usage Data:</strong> We automatically collect information about your device and how you interact with our website.</li>
        </ul>
        <h2>How We Use Your Information</h2>
        <ul>
          <li>To send you our newsletter (if subscribed)</li>
          <li>To respond to your comments and inquiries</li>
          <li>To improve our website and content</li>
        </ul>
        <h2>Cookies</h2>
        <p>We use cookies to remember your preferences (like dark mode) and to analyze website traffic.</p>
        <h2>Third-Party Services</h2>
        <p>We may use third-party services for analytics and newsletter delivery. These services have their own privacy policies.</p>
        <h2>Contact Us</h2>
        <p>If you have any questions about this Privacy Policy, please contact us through our Contact page.</p>
      </div>
    </div>
  </main>
);

// Terms of Service Page
const TermsOfServicePage = () => (
  <main className="py-12 px-4 sm:px-6" data-testid="terms-page">
    <div className="max-w-3xl mx-auto">
      <h1 className="text-4xl font-serif font-bold mb-6">Terms of Service</h1>
      <div className="prose max-w-none">
        <p><em>Last updated: {new Date().toLocaleDateString()}</em></p>
        <h2>Agreement to Terms</h2>
        <p>By accessing This and That Stories, you agree to be bound by these Terms of Service and all applicable laws and regulations.</p>
        <h2>Intellectual Property</h2>
        <p>All content on this website, including text, images, and graphics, is the property of This and That Stories and is protected by copyright laws. You may not reproduce, distribute, or create derivative works without our written permission.</p>
        <h2>User Comments</h2>
        <p>By posting comments on our website, you grant us a non-exclusive license to use, reproduce, and display your comments. You are responsible for the content of your comments and must not post anything that is unlawful, offensive, or infringes on others' rights.</p>
        <h2>Disclaimer</h2>
        <p>The content on this website is for entertainment and informational purposes only. We make no warranties about the accuracy or completeness of the content.</p>
        <h2>Limitation of Liability</h2>
        <p>This and That Stories shall not be liable for any damages arising from your use of this website.</p>
        <h2>Changes to Terms</h2>
        <p>We reserve the right to modify these terms at any time. Continued use of the website constitutes acceptance of any changes.</p>
        <h2>Contact</h2>
        <p>For questions about these Terms, please contact us through our Contact page.</p>
      </div>
    </div>
  </main>
);

// ============= ADMIN PANEL =============

// Admin Login Page
const AdminLogin = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (token) {
      axios.get(`${API}/admin/verify?token=${token}`)
        .then(() => navigate('/admin/dashboard'))
        .catch(() => localStorage.removeItem('adminToken'));
    }
  }, [navigate]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const response = await axios.post(`${API}/admin/login`, { username, password });
      localStorage.setItem('adminToken', response.data.token);
      toast.success('Login successful!');
      navigate('/admin/dashboard');
    } catch (error) {
      toast.error('Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted p-4" data-testid="admin-login">
      <div className="bg-card p-8 rounded-xl shadow-lg w-full max-w-md border border-border">
        <div className="text-center mb-8">
          <LayoutDashboard className="h-12 w-12 mx-auto text-primary mb-4" />
          <h1 className="text-2xl font-serif font-bold">Admin Login</h1>
          <p className="text-muted-foreground">This and That Stories</p>
        </div>
        <form onSubmit={handleLogin} className="space-y-4">
          <div>
            <Label htmlFor="username">Username</Label>
            <Input id="username" value={username} onChange={(e) => setUsername(e.target.value)} required className="mt-1" data-testid="admin-username" />
          </div>
          <div>
            <Label htmlFor="password">Password</Label>
            <Input id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="mt-1" data-testid="admin-password" />
          </div>
          <Button type="submit" className="w-full" disabled={loading} data-testid="admin-login-btn">
            {loading ? 'Logging in...' : 'Login'}
          </Button>
        </form>
        <div className="mt-6 text-center">
          <Link to="/" className="text-sm text-muted-foreground hover:text-primary">← Back to website</Link>
        </div>
      </div>
    </div>
  );
};

// Admin Dashboard
const AdminDashboard = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [showSettings, setShowSettings] = useState(false);
  const [bannerUploading, setBannerUploading] = useState(false);
  const [bannerUrl, setBannerUrl] = useState('');
  const navigate = useNavigate();
  const token = localStorage.getItem('adminToken');

  useEffect(() => {
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchData();
    // Load current banner URL from localStorage
    const savedBanner = localStorage.getItem('homepageBanner');
    if (savedBanner) setBannerUrl(savedBanner);
  }, [token, navigate]);

  const handleBannerUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setBannerUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload?token=${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setBannerUrl(response.data.url);
      localStorage.setItem('homepageBanner', response.data.url);
      toast.success('Banner uploaded! Refresh homepage to see changes.');
    } catch (error) {
      toast.error('Failed to upload banner');
    } finally {
      setBannerUploading(false);
    }
  };

  const fetchData = async () => {
    try {
      const [postsRes, categoriesRes, statsRes] = await Promise.all([
        axios.get(`${API}/admin/posts?token=${token}`),
        axios.get(`${API}/categories`),
        axios.get(`${API}/stats`)
      ]);
      setPosts(postsRes.data);
      setCategories(categoriesRes.data);
      setStats(statsRes.data);
    } catch (error) {
      if (error.response?.status === 401) {
        localStorage.removeItem('adminToken');
        navigate('/admin');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    navigate('/admin');
    toast.success('Logged out');
  };

  const handleDelete = async (postId) => {
    try {
      await axios.delete(`${API}/admin/posts/${postId}?token=${token}`);
      toast.success('Post deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete post');
    }
    setDeleteConfirm(null);
  };

  if (loading) {
    return <div className="min-h-screen flex items-center justify-center"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div></div>;
  }

  return (
    <div className="min-h-screen bg-muted" data-testid="admin-dashboard">
      {/* Admin Header */}
      <header className="bg-card border-b border-border sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <LayoutDashboard className="h-6 w-6 text-primary" />
            <h1 className="text-xl font-serif font-bold">Admin Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <Button variant="outline" size="sm" onClick={() => setShowSettings(true)}>
              <Settings className="h-4 w-4 mr-2" />Settings
            </Button>
            <Link to="/" target="_blank">
              <Button variant="outline" size="sm">View Site</Button>
            </Link>
            <Button variant="ghost" size="sm" onClick={handleLogout}>
              <LogOut className="h-4 w-4 mr-2" />Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-card p-4 rounded-xl border border-border">
            <p className="text-muted-foreground text-sm">Total Posts</p>
            <p className="text-3xl font-bold text-primary">{stats.posts || 0}</p>
          </div>
          <div className="bg-card p-4 rounded-xl border border-border">
            <p className="text-muted-foreground text-sm">Categories</p>
            <p className="text-3xl font-bold text-primary">{stats.categories || 0}</p>
          </div>
          <div className="bg-card p-4 rounded-xl border border-border">
            <p className="text-muted-foreground text-sm">Comments</p>
            <p className="text-3xl font-bold text-primary">{stats.comments || 0}</p>
          </div>
          <div className="bg-card p-4 rounded-xl border border-border">
            <p className="text-muted-foreground text-sm">Subscribers</p>
            <p className="text-3xl font-bold text-primary">{stats.subscribers || 0}</p>
          </div>
        </div>

        {/* Posts Section */}
        <div className="bg-card rounded-xl border border-border">
          <div className="p-4 border-b border-border flex items-center justify-between">
            <h2 className="text-lg font-serif font-bold flex items-center gap-2">
              <FileText className="h-5 w-5" />All Posts
            </h2>
            <Button onClick={() => setIsCreating(true)} data-testid="create-post-btn">
              <Plus className="h-4 w-4 mr-2" />New Post
            </Button>
          </div>
          <div className="divide-y divide-border">
            {posts.map(post => (
              <div key={post.id} className="p-4 flex items-center gap-4 hover:bg-muted/50 transition-colors">
                <img src={post.featured_image} alt="" className="w-16 h-12 object-cover rounded" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-medium truncate">{post.title}</h3>
                  <p className="text-sm text-muted-foreground">
                    {post.category_name} • {new Date(post.created_at).toLocaleDateString()} • {post.views} views
                    {!post.published && <span className="ml-2 text-yellow-600">(Draft)</span>}
                    {post.featured && <span className="ml-2 text-primary">(Featured)</span>}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={() => setEditingPost(post)} data-testid={`edit-post-${post.id}`}>
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button variant="outline" size="sm" className="text-destructive hover:text-destructive" onClick={() => setDeleteConfirm(post.id)} data-testid={`delete-post-${post.id}`}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            ))}
            {posts.length === 0 && (
              <div className="p-8 text-center text-muted-foreground">
                No posts yet. Create your first post!
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Edit/Create Post Modal */}
      <PostEditor 
        post={editingPost} 
        isOpen={!!editingPost || isCreating} 
        onClose={() => { setEditingPost(null); setIsCreating(false); }}
        categories={categories}
        token={token}
        onSave={fetchData}
      />

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteConfirm} onOpenChange={() => setDeleteConfirm(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Post?</AlertDialogTitle>
            <AlertDialogDescription>This action cannot be undone. The post will be permanently deleted.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={() => handleDelete(deleteConfirm)} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Settings Modal */}
      <Dialog open={showSettings} onOpenChange={setShowSettings}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-serif flex items-center gap-2">
              <Settings className="h-6 w-6" />
              Site Settings
            </DialogTitle>
            <DialogDescription>Customize your blog's appearance</DialogDescription>
          </DialogHeader>
          <div className="space-y-6 py-4">
            <div>
              <Label>Homepage Banner Image</Label>
              <p className="text-sm text-muted-foreground mb-3">Upload a new banner image for your homepage</p>
              <div className="space-y-3">
                <div className="flex gap-2">
                  <label htmlFor="banner-upload" className="flex-1">
                    <Button type="button" variant="outline" disabled={bannerUploading} className="w-full" asChild>
                      <span className="cursor-pointer">
                        <Image className="h-4 w-4 mr-2" />
                        {bannerUploading ? 'Uploading...' : 'Upload New Banner'}
                      </span>
                    </Button>
                  </label>
                  <input 
                    id="banner-upload" 
                    type="file" 
                    accept="image/*" 
                    onChange={handleBannerUpload}
                    className="hidden"
                  />
                </div>
                {bannerUrl && (
                  <div className="border rounded-lg p-2">
                    <p className="text-xs text-muted-foreground mb-2">Current Banner Preview:</p>
                    <img src={bannerUrl} alt="Banner preview" className="w-full h-32 object-contain bg-[#f8f4ef] rounded" />
                  </div>
                )}
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Post Editor Component
const PostEditor = ({ post, isOpen, onClose, categories, token, onSave }) => {
  const [form, setForm] = useState({
    title: '',
    excerpt: '',
    content: '',
    featured_image: '',
    category_id: '',
    tags: [],
    featured: false,
    published: true
  });
  const [saving, setSaving] = useState(false);
  const [tagInput, setTagInput] = useState('');
  const [uploading, setUploading] = useState(false);

  const handleImageUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API}/upload?token=${token}`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      setForm({ ...form, featured_image: response.data.url });
      toast.success('Image uploaded successfully!');
    } catch (error) {
      toast.error('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  useEffect(() => {
    if (post) {
      setForm({
        title: post.title || '',
        excerpt: post.excerpt || '',
        content: post.content || '',
        featured_image: post.featured_image || '',
        category_id: post.category_id || '',
        tags: post.tags || [],
        featured: post.featured || false,
        published: post.published !== false
      });
    } else {
      setForm({
        title: '',
        excerpt: '',
        content: '',
        featured_image: '',
        category_id: categories[0]?.id || '',
        tags: [],
        featured: false,
        published: true
      });
    }
  }, [post, categories]);

  const handleSave = async () => {
    if (!form.title || !form.content || !form.category_id) {
      toast.error('Please fill in title, content, and category');
      return;
    }
    setSaving(true);
    try {
      if (post) {
        await axios.put(`${API}/admin/posts/${post.id}?token=${token}`, form);
        toast.success('Post updated!');
      } else {
        await axios.post(`${API}/admin/posts?token=${token}`, form);
        toast.success('Post created!');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save post');
    } finally {
      setSaving(false);
    }
  };

  const addTag = () => {
    if (tagInput && !form.tags.includes(tagInput.toLowerCase())) {
      setForm({ ...form, tags: [...form.tags, tagInput.toLowerCase()] });
      setTagInput('');
    }
  };

  const removeTag = (tag) => {
    setForm({ ...form, tags: form.tags.filter(t => t !== tag) });
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto" data-testid="post-editor">
        <DialogHeader>
          <DialogTitle className="text-2xl font-serif">{post ? 'Edit Post' : 'Create New Post'}</DialogTitle>
        </DialogHeader>
        <div className="space-y-6 py-4">
          <div>
            <Label htmlFor="title">Title *</Label>
            <Input id="title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Enter post title" className="mt-1" data-testid="post-title-input" />
          </div>

          <div>
            <Label htmlFor="excerpt">Excerpt</Label>
            <Textarea id="excerpt" value={form.excerpt} onChange={(e) => setForm({ ...form, excerpt: e.target.value })} placeholder="Short description..." className="mt-1 min-h-[80px]" data-testid="post-excerpt-input" />
          </div>

          <div>
            <Label htmlFor="content">Content * (HTML supported)</Label>
            <Textarea id="content" value={form.content} onChange={(e) => setForm({ ...form, content: e.target.value })} placeholder="Write your post content..." className="mt-1 min-h-[200px] font-mono text-sm" data-testid="post-content-input" />
          </div>

          <div>
            <Label htmlFor="featured_image">Featured Image</Label>
            <div className="space-y-2 mt-1">
              <div className="flex gap-2">
                <Input 
                  id="featured_image" 
                  value={form.featured_image} 
                  onChange={(e) => setForm({ ...form, featured_image: e.target.value })} 
                  placeholder="Image URL or upload below..." 
                  data-testid="post-image-input" 
                />
                <label htmlFor="file-upload">
                  <Button type="button" variant="outline" disabled={uploading} asChild>
                    <span className="cursor-pointer">
                      <Image className="h-4 w-4 mr-2" />
                      {uploading ? 'Uploading...' : 'Upload'}
                    </span>
                  </Button>
                </label>
                <input 
                  id="file-upload" 
                  type="file" 
                  accept="image/*" 
                  onChange={handleImageUpload}
                  className="hidden"
                />
              </div>
              {form.featured_image && (
                <img src={form.featured_image} alt="Preview" className="w-32 h-24 object-cover rounded border" />
              )}
            </div>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Category *</Label>
              <Select value={form.category_id} onValueChange={(val) => setForm({ ...form, category_id: val })}>
                <SelectTrigger className="mt-1" data-testid="post-category-select">
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map(cat => (
                    <SelectItem key={cat.id} value={cat.id}>{cat.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div>
              <Label>Tags</Label>
              <div className="flex gap-2 mt-1">
                <Input value={tagInput} onChange={(e) => setTagInput(e.target.value)} placeholder="Add tag" onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTag())} />
                <Button type="button" variant="outline" onClick={addTag}>Add</Button>
              </div>
              <div className="flex flex-wrap gap-2 mt-2">
                {form.tags.map(tag => (
                  <span key={tag} className="px-2 py-1 bg-muted rounded-full text-sm flex items-center gap-1">
                    #{tag}
                    <button onClick={() => removeTag(tag)} className="hover:text-destructive">×</button>
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="flex items-center gap-6">
            <div className="flex items-center gap-2">
              <Switch id="featured" checked={form.featured} onCheckedChange={(val) => setForm({ ...form, featured: val })} />
              <Label htmlFor="featured">Featured Post</Label>
            </div>
            <div className="flex items-center gap-2">
              <Switch id="published" checked={form.published} onCheckedChange={(val) => setForm({ ...form, published: val })} />
              <Label htmlFor="published">Published</Label>
            </div>
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-border">
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-post-btn">
            <Save className="h-4 w-4 mr-2" />{saving ? 'Saving...' : 'Save Post'}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};

// Main App
function App() {
  const [newsletterOpen, setNewsletterOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);

  return (
    <ThemeProvider>
      <div className="App min-h-screen bg-background">
        <BrowserRouter>
          <Routes>
            {/* Admin Routes - No Navigation/Footer */}
            <Route path="/admin" element={<AdminLogin />} />
            <Route path="/admin/dashboard" element={<AdminDashboard />} />
            
            {/* Public Routes */}
            <Route path="/*" element={
              <>
                <Navigation onOpenSearch={() => setSearchOpen(true)} />
                <Routes>
                  <Route path="/" element={<Home />} />
                  <Route path="/post/:slug" element={<PostPage />} />
                  <Route path="/category/:slug" element={<CategoryPage />} />
                  <Route path="/all-stories" element={<AllStoriesPage />} />
                  <Route path="/about" element={<AboutPage />} />
                  <Route path="/book" element={<BookPage />} />
                  <Route path="/contact" element={<ContactPage />} />
                  <Route path="/privacy-policy" element={<PrivacyPolicyPage />} />
                  <Route path="/terms-of-service" element={<TermsOfServicePage />} />
                </Routes>
                <Footer onOpenNewsletter={() => setNewsletterOpen(true)} />
                <NewsletterModal isOpen={newsletterOpen} onClose={() => setNewsletterOpen(false)} />
                <SearchModal isOpen={searchOpen} onClose={() => setSearchOpen(false)} />
              </>
            } />
          </Routes>
          <Toaster position="bottom-right" richColors />
        </BrowserRouter>
      </div>
    </ThemeProvider>
  );
}

export default App;
